<template>
  <div v-if="enabled" :data-testid="dataTestid">
    <c-page-section squares>
      <div class="full-width alert" :class="[{ 'alert-hoverable': hoverable }, alert.backgroundClass]">
        <div class="icon-section">
          <q-icon :name="alert.icon" size="40px" :class="alert.cssClass" class="alert-icon" />
        </div>
        <div class="text-section">
          <span> {{ message ? message : t(translateMessage) }} </span>
          <q-space />
        </div>
        <div v-if="!hideCloseButton" class="close-section">
          <q-btn icon="close" @click="clickHandler" />
        </div>
      </div>
    </c-page-section>
  </div>
</template>

<script lang="ts" setup>
import { toRefs, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import CPageSection from '@/components/_common/PageSection.vue';

type alertType = 'warning' | 'info' | 'success' | 'bell';
const ALERTS: {
  type: alertType;
  icon: string;
  cssClass: string;
  backgroundClass: string;
}[] = [
  {
    type: 'warning',
    icon: 'warning',
    cssClass: 'text-negative icon-warning',
    backgroundClass: '',
  },
  {
    type: 'info',
    icon: 'info',
    cssClass: 'text-info icon-info',
    backgroundClass: '',
  },
  {
    type: 'success',
    icon: 'check_circle',
    cssClass: 'text-positive icon-success',
    backgroundClass: '',
  },
  {
    type: 'bell',
    icon: 'mdi-bell-alert-outline',
    cssClass: 'text-black icon-alert',
    backgroundClass: 'bell-background',
  },
];

const BLANK_ALERT = {
  type: '',
  icon: '',
  cssClass: '',
  backgroundClass: '',
};

/*****************************************************************************
 * PROPS
 ****************************************************************************/
const props = withDefaults(
  defineProps<{
    translateMessage?: string;
    message?: string;
    type?: alertType;
    show?: boolean;
    hideCloseButton?: boolean;
    hoverable?: boolean;
    dataTestid?: string;
  }>(),
  {
    translateMessage: '',
    message: '',
    type: 'info',
    show: false,
    hideCloseButton: false,
    hoverable: false,
    dataTestid: 'alert',
  },
);

/*****************************************************************************
 * EMITS
 ****************************************************************************/
const emit = defineEmits(['update:show']);

/*****************************************************************************
 * SETUP
 ****************************************************************************/
const { type, show } = toRefs(props);
const { t } = useI18n();

const enabled = ref(props.show);

const alert = computed(() => {
  const item = ALERTS.find((alert) => alert.type === type.value);
  return item || BLANK_ALERT;
});

const clickHandler = () => {
  enabled.value = !enabled.value;
  emit('update:show', enabled.value);
};

watch(show, () => {
  enabled.value = show.value;
});
</script>
<style scoped lang="scss">
@import 'styles/app.variables.scss';
.alert {
  display: flex;
  box-sizing: border-box;

  .icon-section {
    align-items: center;
    justify-content: center;
    display: flex;
    padding: 0 20px;
  }

  .text-section {
    width: 100%;
    padding: 22px 20px 22px 0;
  }
  .close-section {
    width: 50px;
    button {
      width: 100%;
      height: 100%;
    }
    button:before {
      box-shadow: unset;
    }
  }
  .close-section:deep(button > span.q-btn__content) {
    i {
      font-size: $text-regular-size;
    }
  }
}
.body--dark {
  .alert {
    box-shadow: 0 0 10px nth($app-shadow-color, 1);
  }
  .text-section {
    color: nth($text-color, 1);
  }
  .close-section {
    border-left: 1px nth($border-color, 1) solid;
  }
}

.body--light {
  .alert {
    box-shadow: 0 0 10px nth($app-shadow-color, 2);
  }
  .text-section {
    color: nth($text-color, 2);
  }
  .close-section {
    border-left: 1px nth($border-color, 2) solid;
  }
}

.alert-icon {
  z-index: 2;
}

.icon-warning:before {
  position: absolute;
  z-index: -1;
  content: '';
  top: 4px;
  left: 3px;
  width: 0;
  height: 0;
  border-left: 17px solid transparent;
  border-right: 17px solid transparent;
  border-bottom: 30px solid $light-grey;
}

.icon-warning.size-18:before {
  top: 3px;
  left: 2px;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-bottom: 12px solid $light-grey;
}

.icon-warning:after {
  z-index: -2;
  top: 0;
  filter: blur(4px);
  position: absolute;
  font-size: 40px;
  content: 'warning';
}

.icon-info:before,
.icon-success:before {
  position: absolute;
  z-index: -1;
  content: '';
  top: 4px;
  left: 4px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: $light-grey;
}

.icon-info:after,
.icon-success:after {
  z-index: -2;
  top: 0;
  filter: blur(4px);
  position: absolute;
  font-size: 40px;
}

.icon-info:after {
  content: 'info';
}

.alert-hoverable {
  cursor: pointer;
  transition: background-color 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}
.bell-background {
  background-color: rgb(233, 135, 43);
  .text-section {
    span {
      color: black;
    }
  }
}
.alert-hoverable.bell-background:hover {
  background-color: rgb(255, 195, 140);
}
</style>
